import {
  linesOf,
  sentencesOf,
} from "@/lib/tool-kit/research-workflow/document";

/**
 * The plain-language checks the consent and debriefing generators run over what the
 * researcher typed.
 *
 * Two checks, both deliberately mechanical: sentences longer than a limit, and words
 * from a list of research jargon with a plainer alternative beside each. Neither is a
 * readability score — a short sentence full of "efficacy" and "randomized" still loses a
 * participant — so the result is a list of specific things to rewrite, not a grade.
 */

export interface JargonPair {
  term: string;
  plain: string;
}

export interface LongSentence {
  /** The sentence's opening words, for the reader to find it. */
  start: string;
  words: number;
}

const WORD = /[\p{L}\p{N}\p{M}'’-]+/gu;
const CURLY_APOSTROPHE = /’/g;
const PAIR_SEPARATOR = " = ";

/** Lower-cased word tokens. Hyphenated words ("de-identified") stay one token. */
export function wordsOf(text: string): string[] {
  return (text.toLowerCase().match(WORD) ?? []).map((word) =>
    word.replace(CURLY_APOSTROPHE, "'")
  );
}

/** Whether `phrase` occurs in `text` as whole words, ignoring case and apostrophe style. */
export function containsPhrase(text: string, phrase: string): boolean {
  return containsSequence(wordsOf(text), wordsOf(phrase));
}

/** `"utilize = use"` → `{ term: "utilize", plain: "use" }`. Malformed lines are skipped. */
export function parseJargonPairs(lines: readonly string[]): JargonPair[] {
  const pairs: JargonPair[] = [];
  for (const line of lines) {
    const at = line.indexOf(PAIR_SEPARATOR);
    if (at <= 0) {
      continue;
    }
    const term = line.slice(0, at).trim();
    const plain = line.slice(at + PAIR_SEPARATOR.length).trim();
    if (term && plain) {
      pairs.push({ term, plain });
    }
  }
  return pairs;
}

function containsSequence(haystack: string[], needle: string[]): boolean {
  if (needle.length === 0 || needle.length > haystack.length) {
    return false;
  }
  for (let i = 0; i + needle.length <= haystack.length; i++) {
    let match = true;
    for (let j = 0; j < needle.length; j++) {
      if (haystack[i + j] !== needle[j]) {
        match = false;
        break;
      }
    }
    if (match) {
      return true;
    }
  }
  return false;
}

/**
 * The jargon terms that occur in `text`, as whole words or whole phrases, in list order.
 * "prior to" matches "Prior to the session"; "terminate" does not match "terminated".
 */
export function findJargon(
  text: string,
  pairs: readonly JargonPair[]
): JargonPair[] {
  const words = wordsOf(text);
  return pairs.filter((pair) => containsSequence(words, wordsOf(pair.term)));
}

/**
 * Sentences line by line: each box (and each line in it) is segmented on its own, so a
 * fragment without a full stop — "about 20 minutes" — never runs on into the next box.
 */
function sentencesByLine(text: string, locale: string): string[] {
  return linesOf(text).flatMap((line) => sentencesOf(line, locale));
}

/** Sentences over `maxWords` words, with their first few words for orientation. */
export function longSentences(
  text: string,
  maxWords = 25,
  locale = "en"
): LongSentence[] {
  const out: LongSentence[] = [];
  for (const sentence of sentencesByLine(text, locale)) {
    const words = sentence.split(" ").filter((word) => word.length > 0);
    if (words.length > maxWords) {
      out.push({ start: words.slice(0, 8).join(" "), words: words.length });
    }
  }
  return out;
}

/** Mean words per sentence, or 0 for empty text. */
export function meanSentenceLength(text: string, locale = "en"): number {
  const sentences = sentencesByLine(text, locale);
  if (sentences.length === 0) {
    return 0;
  }
  const total = sentences.reduce(
    (sum, sentence) =>
      sum + sentence.split(" ").filter((word) => word.length > 0).length,
    0
  );
  return total / sentences.length;
}
